import React from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { isNumber, isString, map, round, truncate } from 'lodash';
import { customTheme } from 'theme';

import formatNumber, {
  chartNumberFormatter,
} from 'components/DataDisplay/Utils/formatNumber';

import { limitedString } from '../../../../utils/string';
const colors = customTheme.colors;
export const LIGHT_COLORS = [
  colors.blue['200'],
  colors.purple['200'],
  colors.skyBlue['200'],
  colors.green['200'],
  colors.red['200'],
  colors.yellow['200'],
];

export const DARK_COLORS = [
  colors.blue['300'],
  colors.purple['300'],
  colors.skyBlue['300'],
  colors.green['300'],
  colors.red['300'],
  colors.yellow['300'],
];

export const customizedXAxisTick = props => {
  const { x, y, payload, style, sliceLength = 8 } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={10}
        fill={customTheme.colors.gray['300']}
        textAnchor="middle"
        fontSize={14}
        fontWeight={400}
        {...style}
      >
        {isString(payload.value)
          ? limitedString(payload.value, sliceLength)
          : payload.value}
      </text>
    </g>
  );
};

export const rotatedXAxisTick = props => {
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={2}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-38)"
        fontSize="14px"
      >
        {payload.value}
      </text>
    </g>
  );
};

export const customizedYAxisTick = (props, divideBy: number = 0) => {
  const { x, y, payload, style } = props;
  return (
    <g transform={`translate(${x},${y + 2})`}>
      <text
        x={0}
        fill="#666"
        alignmentBaseline="middle"
        textAnchor="end"
        fontSize={14}
        {...style}
      >
        {!!divideBy
          ? round(payload.value / divideBy)
          : isNumber(payload.value)
          ? chartNumberFormatter(payload.value, 2)
          : payload.value}
      </text>
    </g>
  );
};

export const customizedYAxisTickWithFormatNumber = props => {
  const { x, y, payload, style } = props;
  return (
    <g transform={`translate(${x},${y + 2})`}>
      <text
        x={0}
        fill="#666"
        alignmentBaseline="middle"
        textAnchor="end"
        fontSize={14}
        {...style}
      >
        {formatNumber(payload.value)}
      </text>
    </g>
  );
};

export const customizedRightYAxisTick = props => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y + 4})`}>
      <text fill="#666" fontSize={12}>
        {payload.value}
      </text>
    </g>
  );
};

export const customizedLegend = (value, entry, index) => {
  return (
    <span style={{ fontSize: '14px', color: '#666' }}>
      {truncate(value, { length: 20 })}
    </span>
  );
};

export const customLegends = props => {
  return (
    <Stack
      h="full"
      justify="center"
      w={props?.width}
      fontSize={props?.fontSize ?? 'sm'}
    >
      {map(props.payload, o => (
        <HStack
          // Optional-call onClick so legends rendered without a handler do
          // not throw on click.
          onClick={() => props?.onClick?.(o)}
          cursor={!!props?.onClick ? 'pointer' : 'default'}
        >
          <Box boxSize={2.5} bg={o.color} borderRadius={2} />
          <Box flex={1}>
            {o?.value} ({formatNumber(o?.payload?.value, 1)})
          </Box>
        </HStack>
      ))}
    </Stack>
  );
};

export const sortedLineChartTooltip = props => {
  const { active, payload, label } = props;
  if (active && payload && payload.length) {
    // Sort by value descending (customize as needed)
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

    return (
      <Box
        bg="white"
        border="1px solid #ccc"
        px={4}
        borderRadius={6}
        fontSize="sm"
        py={2}
      >
        <Stack spacing={1.5}>
          <Box>{label}</Box>
          <Stack spacing={0.5}>
            {sortedPayload.map((entry, index) => (
              <Box color={entry.color} key={index}>
                {entry.name}:{' '}
                {isNumber(entry.value)
                  ? entry.value.toLocaleString('en-US')
                  : entry.value}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  }

  return null;
};
